import { useState, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import type { DashboardFilters } from '@/types/dashboard';
import DashboardHeader from './DashboardHeader';
import FilterPanel from './FilterPanel';
import StatsRow from './StatsRow';
import ConflictAlert from './ConflictAlert';
import StudentTable from './StudentTable';
import { useExportMagicLinkReport, useExportSchoolReport, useGenerateMagicLinkReport, useGenerateSchoolReport, useSchoolBatches, useSchoolDetails, useSchoolStudents } from '@/hooks/useSchool';
import { generateMagicLinkReportPayload, generateSchoolReportPayload } from '@/types/school';
import SuccessSnackbar from '@/components/SuccessSnackbar';
import { NoFilterTablePlaceholder } from '@/components/ui/NoFilterTablePlaceholder';
import { SCHOOL_ID_LENGTH } from '@/constants/schoolConstants';

const INITIAL_FILTERS: DashboardFilters = {
  schoolId: '',
  batches: [],
  examDate: '',
};

export default function DashboardPage(): JSX.Element {
  // ── Filters ────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<DashboardFilters>(INITIAL_FILTERS);
  const [schoolId, setSchoolId] = useState('');

  const handleFilterChange = useCallback((updated: Partial<DashboardFilters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));

    if (updated.schoolId !== undefined && updated.schoolId.trim().length !== SCHOOL_ID_LENGTH) {
      setSchoolId('');
      setFilters((prev) => ({ ...prev, examDate: '' }));
    }
  }, []);

  const { data: schoolDetails, isLoading: isSchoolDetailsLoading, isSuccess: isSchoolDetailsSuccess, error: schoolDetailsError } =
    useSchoolDetails(schoolId);

  const { data: batchesData, isLoading: isBatchesDataLoading, error: batchesError, refetch: refetchSchoolBatches } = useSchoolBatches(schoolId, isSchoolDetailsSuccess);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      batches: batchesData ?? [],
      selectedBatches: [],
    }));
  }, [batchesData]);

  const onGetSchoolDataClick = () => {
    const trimmedSchoolId = filters.schoolId.trim();

    if (trimmedSchoolId === schoolId) {
      // Same School ID re-submitted — force a fresh fetch instead of serving cached data.
      refetchSchoolBatches();
      return;
    }

    setSchoolId(trimmedSchoolId);
  };

  const { mutate: exportMagicLinkReport } = useExportMagicLinkReport();
  const { mutate: exportSchoolLoginReport } = useExportSchoolReport();
  const {
    mutate: generateMagicLinkReport,
    isError: isMagicLinkReportError,
    isSuccess: isMagicLinkReportSuccess,
    errorDescription: magicLinkReportErrorDescription,
  } = useGenerateMagicLinkReport();
  const {
    mutate: generateSchoolReport,
    isSuccess: isSchoolReportSuccess,
  } = useGenerateSchoolReport();
  const { data: rows, isLoading: isStudentsListLoading, error: studentsListError } = useSchoolStudents(
    schoolId,
    isSchoolDetailsSuccess,
    { batch_ids: filters.selectedBatches, exam_date: filters.examDate }
  );
  const [showConflictAlert, setShowConflictAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isMagicLinkReportError) {
      setShowConflictAlert(false);
      return;
    }

    setShowConflictAlert(true);
    const timeoutId = window.setTimeout(() => setShowConflictAlert(false), 5000);

    return () => window.clearTimeout(timeoutId);
  }, [isMagicLinkReportError]);

  useEffect(() => {
    if (isMagicLinkReportSuccess) {
      setSuccessMessage('Magic Link report generated successfully.');
    }
  }, [isMagicLinkReportSuccess]);

  useEffect(() => {
    if (isSchoolReportSuccess) {
      setSuccessMessage('School login report generated successfully.');
    }
  }, [isSchoolReportSuccess]);

  const handleExportMagicLinkReport = () => {
    exportMagicLinkReport(schoolId);
  };

  const handleExportSchoolReport = () => {
    exportSchoolLoginReport(schoolId);
  };

  const handleSchoolReportGeneration = () => {
    const payload: generateSchoolReportPayload = {
      batch_ids: filters.selectedBatches ?? [],
      exam_date: filters.examDate,
    };
    generateSchoolReport({ schoolId, payload });
  }

  const handleExport = () => {
    console.log("hello");
  }

  const handleGenerateMagicLinkClick = () =>{
    const payload: generateMagicLinkReportPayload = {
      batch_ids: filters?.selectedBatches ?? []
    }
    generateMagicLinkReport({ schoolId, payload });
  }

  const hasNoFiltersApplied =
    filters.schoolId.trim().length !== SCHOOL_ID_LENGTH ||
    !(filters.selectedBatches && filters.selectedBatches.length > 0);


  return (
    <Box sx={{ pb: 4 }}>
      {/* Page header */}
      <DashboardHeader
        schoolId={schoolId || filters.schoolId.trim()}
        disabled={!schoolId || !isSchoolDetailsSuccess}
        generateSchoolReportDisabled={
          !schoolId || !isSchoolDetailsSuccess || !filters.examDate?.trim()
        }
        onGenerateSchoolReport={handleSchoolReportGeneration}
        onSchoolReport={handleExport}
        onExportCsv={handleExportMagicLinkReport}
        onExportPdf={handleExportSchoolReport}
      />

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onChange={handleFilterChange}
        selectedBatchCount={filters.selectedBatches?.length || 0}
        totalBatchCount={filters?.batches?.length || 0}
        getSchoolData={onGetSchoolDataClick}
        isBatchesLoading={isBatchesDataLoading}
        batchesError={batchesError?.message ?? ''}
      />

      {/* Stats row */}
      { schoolDetails?.id && (
        <StatsRow
          school={schoolDetails}
          isLoading={isSchoolDetailsLoading}
          error={schoolDetailsError}
        />
    )}

      {/* Conflict warning */}
      <ConflictAlert visible={showConflictAlert} description={magicLinkReportErrorDescription} />

      {/* Student table */}
      {hasNoFiltersApplied ? (
        <NoFilterTablePlaceholder />
      ) : (
        <StudentTable
          rows={rows ?? []}
          isLoading={isStudentsListLoading}
          error={studentsListError?.message ?? ''}
          onGenerateMagicLink={handleGenerateMagicLinkClick}
          isGenerateMagicLinkDisabled={hasNoFiltersApplied}
        />
      )}

      {/* Success popup */}
      <SuccessSnackbar
        open={!!successMessage}
        message={successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </Box>
  );
}
